import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";

/* ---------------- TableScroll (PB-5178, d3) ----------------
   The ONE sticky-first-column definition in the design system, plus the scroll
   region a raw <table> uses to get it. `DataTable pinFirstColumn` applies the
   same constants to its own scroll box, so the two never drift, and the recipe
   they reproduce is the one EditableDataGrid already ships — `sticky start-0
   z-raised` body cells, a `z-10` head, a `z-20` corner — which PB-5179 moves
   onto these constants.

   Container-level selectors rather than per-cell classes: the cells belong to
   whoever renders the table (DataTable, a host's own markup), and a pin that
   has to be threaded into every <th>/<td> is a pin somebody forgets. One class
   list on the scroll container pins whatever first column sits inside it.

   States: the pinned cell is OPAQUE (`bg-pb-card`) so scrolled columns never
   bleed through it, and it takes the row's hover and selection tints as
   background-IMAGE layers over that opaque colour — the same `pb-overlay-paint`
   DataTable rows use, the same teal a selected row wears — so the frozen cell
   never reads as a different row from the one it belongs to.

   Width: the region is a size container and the pinned cell's content is
   capped at `min(18rem, 50cqw)`, so a narrow main column (360px at 1024 under
   d10) still keeps half of itself for the scrolling columns. */

/** The focus ring of a scrollable table region — ONE definition, worn by
 * `TableScroll` and by `DataTable pinFirstColumn` alike, so the two cannot
 * drift.
 *
 * The region is a keyboard tab stop because it scrolls sideways (WCAG 2.1.1,
 * and axe's `scrollable-region-focusable`), so the ring has to be there and
 * has to be obvious to whoever is driving from the keyboard. `focus-visible`,
 * not `focus`, keeps it away from a mouse user who merely clicked a cell.
 *
 * `ring-inset` is the whole of the tuning. A Tailwind ring is drawn OUTSIDE
 * the box by default, and two teal pixels out there wrap the table in a halo
 * that reads as an error state on a page with nothing else that colour.
 * `ring-inset` moves those same two pixels onto the table's own edge — as
 * legible for a keyboard user, and quiet enough that arrowing through a table
 * no longer looks like something went wrong. The
 * width stays at 2px: this is the indicator's entire visible area, and
 * thinning it to save noise is the one change that would cost real
 * accessibility. */
export const TABLE_SCROLL_FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-pb-teal/70";

/** Region chrome: the scroll box, its frame and its focus ring. A horizontally
 * scrollable region is a keyboard tab stop (WCAG 2.1.1), hence the ring.
 * `relative` is load-bearing: an `sr-only` label inside the table is
 * absolutely positioned, and with no positioned ancestor its containing block
 * is the viewport — which no overflow box can clip, so a hidden "Actions"
 * header at the far end of a wide table widened the whole page. */
export const TABLE_SCROLL_REGION = `@container relative w-full overflow-x-auto [scrollbar-gutter:stable] rounded-lg border border-pb-border bg-pb-card pb-hairline isolate ${TABLE_SCROLL_FOCUS_RING}`;

/** Pins the first column of any <table> inside the element carrying it. */
export const PIN_FIRST_COLUMN = [
  // The corner: the pinned column's head cell, above everything else in the box.
  "[&_thead_th:first-child]:sticky [&_thead_th:first-child]:start-0 [&_thead_th:first-child]:z-20 [&_thead_th:first-child]:border-e [&_thead_th:first-child]:border-pb-border [&_thead_th:first-child]:bg-pb-card-elevated",
  // Body cells: opaque, one level above the scrolling cells.
  "[&_tbody_td:first-child]:sticky [&_tbody_td:first-child]:start-0 [&_tbody_td:first-child]:z-raised [&_tbody_td:first-child]:border-e [&_tbody_td:first-child]:border-pb-border [&_tbody_td:first-child]:bg-pb-card",
  // Row states, painted as image layers over the opaque colour.
  "[&_tbody_tr:hover>td:first-child]:pb-overlay-paint",
  "[&_tbody_tr[aria-selected=true]>td:first-child]:[background-image:linear-gradient(var(--color-pb-teal-soft),var(--color-pb-teal-soft))]",
].join(" ");

/** The same pin when a selection checkbox column comes first (DataTable
 * `selectable`): the checkbox cell (`w-10`) pins at the start and the first
 * DATA cell pins right after it, carrying the frozen-edge hairline. */
export const PIN_FIRST_COLUMN_AFTER_SELECT = [
  "[&_thead_th:first-child]:sticky [&_thead_th:first-child]:start-0 [&_thead_th:first-child]:z-20 [&_thead_th:first-child]:bg-pb-card-elevated",
  "[&_thead_th:nth-child(2)]:sticky [&_thead_th:nth-child(2)]:start-10 [&_thead_th:nth-child(2)]:z-20 [&_thead_th:nth-child(2)]:border-e [&_thead_th:nth-child(2)]:border-pb-border [&_thead_th:nth-child(2)]:bg-pb-card-elevated",
  "[&_tbody_td:first-child]:sticky [&_tbody_td:first-child]:start-0 [&_tbody_td:first-child]:z-raised [&_tbody_td:first-child]:bg-pb-card",
  "[&_tbody_td:nth-child(2)]:sticky [&_tbody_td:nth-child(2)]:start-10 [&_tbody_td:nth-child(2)]:z-raised [&_tbody_td:nth-child(2)]:border-e [&_tbody_td:nth-child(2)]:border-pb-border [&_tbody_td:nth-child(2)]:bg-pb-card",
  "[&_tbody_tr:hover>td:first-child]:pb-overlay-paint [&_tbody_tr:hover>td:nth-child(2)]:pb-overlay-paint",
  "[&_tbody_tr[aria-selected=true]>td:first-child]:[background-image:linear-gradient(var(--color-pb-teal-soft),var(--color-pb-teal-soft))]",
  "[&_tbody_tr[aria-selected=true]>td:nth-child(2)]:[background-image:linear-gradient(var(--color-pb-teal-soft),var(--color-pb-teal-soft))]",
].join(" ");

/** The same pin worn by a CELL, for the cells the container selectors above
 * cannot reach: they select `tbody td`, so a row header (`th scope="row"`) and
 * a `tfoot` cell carry it on themselves (DataTable `rowHeader` and `footer`).
 * Pair it with the cell's start offset — `start-0`, or `start-10` after
 * DataTable's checkbox column — plus `PIN_FIRST_COLUMN_EDGE` on the pinned
 * data cell, and `PIN_FIRST_COLUMN_ROW_STATES` on a body row header. */
export const PIN_FIRST_COLUMN_CELL = "sticky z-raised bg-pb-card";
/** The frozen-edge hairline on the pinned data cell. */
export const PIN_FIRST_COLUMN_EDGE = "border-e border-pb-border";
/** A pinned body cell's row hover and selection tints, as image layers over
 * its opaque colour — the per-cell form of the `tbody tr` rules above. */
export const PIN_FIRST_COLUMN_ROW_STATES =
  "[tr:hover>&]:pb-overlay-paint [tr[aria-selected=true]>&]:[background-image:linear-gradient(var(--color-pb-teal-soft),var(--color-pb-teal-soft))]";

/** Width cap for the pinned cell's content: the CELL stays within half the
 * scroll region — the content box is capped at half minus the cell's own
 * horizontal padding (1.5rem covers DataTable's comfortable density) — and
 * never wider than 18rem. A raw table's host puts it on the block inside its
 * first cell; DataTable applies it itself. */
export const PIN_FIRST_COLUMN_CONTENT =
  "min-w-0 max-w-[min(18rem,calc(50cqw_-_1.5rem))] overflow-hidden";

export interface TableScrollProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Accessible name of the region. Required: a scrollable region is a tab stop
   * (WCAG 2.1.1) and must say what it holds.
   */
  "aria-label": string;
  /** Pin the first column while the rest scrolls. Default true. */
  pinFirstColumn?: boolean;
  /**
   * Inside a card (`CardBody flush`): drop the region's own border and corner
   * so the card frames the table. The focus ring, the tab stop and the region
   * role stay. Renders what `className="rounded-none border-0"` rendered.
   * Default false.
   */
  embedded?: boolean;
  children: React.ReactNode;
}

/** Horizontal scroll region for a raw `<table>`, with the first column pinned. */
export const TableScroll = forwardRef<HTMLDivElement, TableScrollProps>(
  function TableScroll(
    { pinFirstColumn = true, embedded = false, className, children, ...rest },
    ref,
  ) {
    return (
      <div
        ref={ref}
        role="region"
        // A horizontally scrollable region must be reachable by keyboard (WCAG
        // 2.1.1; axe's scrollable-region-focusable), and a named landmark is
        // the right thing to stop on.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        className={cn(
          TABLE_SCROLL_REGION,
          pinFirstColumn && PIN_FIRST_COLUMN,
          embedded && "rounded-none border-0",
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    );
  },
);
